import merge from 'lodash/merge';
import get from 'lodash/get';
import remove from 'lodash/remove';
import _uniqBy from 'lodash/uniqBy';
import flatten from 'lodash/flatten';
import toNumber from 'lodash/toNumber';
import isEmpty from 'lodash/isEmpty';

import { multiSelectType } from '../components/MultiSelectDropdown';
import {
  getCurrencyFormatter,
  getIdFromGid,
  getGidFromIdForResource,
  uniqBy,
} from './index';
import { emptyGlobalVariant, emptyGlobalProduct } from './globalObjects';
import { getDiscountedPriceURLDiscount, getSellingPlanIdFromURL } from '.';
import { defaultLine } from '../subscriptions/utils';
import { getReviewsFromShopProvider } from './ReviewsProvider';
import { getLineBundleUUID } from '../../components/Subscriptions/utilsDynamicBundles';
import { isUndefined } from 'lodash';

type CustomAttributes = LineItem['CustomAttributes'];

export const formatLiquidProductToGlobalProduct = (
  product: LiquidProduct,
  productsInfo: IndividualProductData[] = [],
  variantsInventory?: { [key: string]: Inventory },
): GlobalProduct | null => {
  const variantsInfo = productsInfo.find(
    (pi) => `${product.id}` === getIdFromGid(pi.Id),
  )?.Variants;

  const variants = variantsInfo
    ? product.variants.filter((v) =>
        variantsInfo
          .map(({ VariantId }) => getIdFromGid(VariantId))
          .includes(`${v.id}`),
      )
    : product.variants;

  if (!variants || variants.length <= 0) return null;
  const optionsAndValues = getOptionsAndValues(product, variants);
  const hasOnlyDefaultVariant = variants.length === 1;
  const reviews: Reviews = getReviewsFromShopProvider(product.metafields);

  const formattedProduct: GlobalProduct = {
    ...emptyGlobalProduct,
    metafields: product.metafields,
    id: product.id,
    handle: product.handle,
    title: product.title,
    type: product.type,
    description: product.description,
    image: product.featured_image,
    images: product.images,
    media: product.media,
    price: +product.price / 100,
    priceMin: product.price_min / 100,
    priceMax: product.price_max / 100,
    discountedPrice: +product.price / 100,
    discountedPriceMin: +product.price_min / 100,
    discountedPriceMax: +product.price_max / 100,
    available: product.available,
    options: product.options,
    tags: product.tags || [],
    optionsAndValues: flatten(optionsAndValues) as MultiSelectOption[],
    hasOnlyDefaultVariant: hasOnlyDefaultVariant,
    variants: variants.map((v) => {
      const inventory = variantsInventory?.[v?.id];
      const variant = { ...v, inventory };

      return transformVariantToGlobal(variant, product, hasOnlyDefaultVariant);
    }),
    variantsOptions: variants.map((v) =>
      transformVariantToMultiSelectOption(v),
    ),
    reviews,
  };

  return formattedProduct;
};

export const formatGlobalVariantInGlobalProduct = (
  variant: GlobalVariant,
  product: GlobalProduct,
): GlobalProduct => {
  const hasOnlyDefaultVariant = true;

  const formattedProduct: GlobalProduct = {
    ...emptyGlobalProduct,
    ...product,
    id: variant.id,
    optionsAndValues: [] as MultiSelectOption[],
    hasOnlyDefaultVariant: hasOnlyDefaultVariant,
    variants: [variant],
    variantsOptions: [variant].map((v) =>
      transformVariantToMultiSelectOption(v),
    ),
  };

  return formattedProduct;
};

const transformVariantToMultiSelectOption = (
  variant: LiquidVariant | GlobalVariant,
  isSelected = false,
): MultiSelectOption => ({
  label: variant.title,
  id: `${variant.id}`,
  type: multiSelectType.OPTION,
  isSelected,
});

const transformVariantToGlobal = (
  variant,
  product,
  hasOnlyDefaultVariant,
): GlobalVariant => {
  const result = { ...emptyGlobalVariant };
  const image = product.featured_image;
  const productName = product.title;
  const productId = product.id;
  const title = hasOnlyDefaultVariant ? product.title : variant.title;
  const reviews: Reviews = getReviewsFromShopProvider(product.metafields);
  const originalVariantTitle = variant.title;

  return {
    ...result,
    discountedPrice: +product.price / 100,
    id: variant.id,
    name: variant.name,
    available: variant.available,
    inventory: variant.inventory,
    productName,
    title,
    productId,
    options: variant.options,
    price: +variant.price / 100,
    image: variant.featured_image ? variant.featured_image.src : image,
    description: product.description,
    sellingPlanAllocations: variant.selling_plan_allocations,
    selectedOptions: [],
    originalVariantTitle,
    media: product.media,
    reviews,
  };
};

const getOptionsAndValues = (productData, variants: LiquidVariant[]) => {
  const options = productData.options;
  const options1 = new Set();
  const options2 = new Set();
  const options3 = new Set();
  variants?.forEach((variant) => {
    options1.add(variant.option1);
    options2.add(variant.option2);
    options3.add(variant.option3);
  });

  const optionsAndValues = options.map((optionName, i) => {
    const optionValue = [options1, options2, options3];
    const variantOption = [
      productData.variants[0].option1,
      productData.variants[0].option2,
      productData.variants[0].option3,
    ];
    const options = Array.from(optionValue[i]).map((name) => {
      return {
        label: name,
        id: name,
        type: 'option',
        optionName,
        isSelected: variantOption[i] === name,
      };
    });
    return [{ label: optionName, id: optionName, type: 'title' }, ...options];
  });

  return optionsAndValues;
};

export const extractVariantAndImage = (
  {
    productData = {} as LiquidProduct,
    productMetafields = {},
    variantsMetafields = {},
    variantsInventory = {},
    hasOnlyDefaultVariant,
  }: LiquidProductInCollection,
  isAddon = false,
): GlobalVariant[] => {
  if (productData?.error) return [] as GlobalVariant[];
  const result = { ...emptyGlobalVariant };
  const hasOneVariant = productData?.variants?.length === 1;
  const image = productData.featured_image;
  const productId = productData.id;
  const productName = productData.title;

  const reviews: Reviews = getReviewsFromShopProvider(productMetafields);

  return productData?.variants?.map((variant) => {
    const title =
      hasOnlyDefaultVariant || hasOneVariant
        ? productData.title
        : variant.title;
    const originalVariantTitle = variant.title;

    const baBSellingPlan = !isAddon ? generateSellingPlanFromBaB(variant) : [];
    const { type } = productData;
    return {
      ...result,
      id: variant.id,
      name: variant.name,
      available: variant.available,
      productName,
      title,
      productId,
      tags: productData.tags,
      handle: productData.handle,
      options: variant.options,
      price: +variant.price / 100,
      image: variant.featured_image ? variant.featured_image.src : image,
      images: productData.images,
      description: productData.description,
      sellingPlanAllocations: [
        ...(variant.selling_plan_allocations || []),
        ...baBSellingPlan,
      ],
      hasOnlyDefaultVariant: hasOnlyDefaultVariant || hasOneVariant,
      metafields: merge({}, productMetafields, variantsMetafields[variant.id]),
      inventory: variantsInventory[variant.id],
      originalVariantTitle,
      type,
      reviews,
    };
  });
};

export const getOnlySelectedVariants = (
  variant: GlobalVariant,
  variants: IndividualProductData['Variants'] | null = [],
): boolean =>
  variants?.some((v) => getIdFromGid(v?.VariantId) === `${variant.id}`) ??
  false;

const generateSellingPlanFromBaB = (variant: LiquidVariant) => {
  const price = getDiscountedPriceURLDiscount(+variant.price);
  const sellingPlanId = getSellingPlanIdFromURL();

  if (!sellingPlanId) return [];

  const sellingPlanAllocations: SellingPlanAllocations[] = [
    {
      selling_plan_id: Number(sellingPlanId),
      price,
      compare_at_price: +variant.price,
      selling_plan_group_id: 'default',
    },
  ];

  return sellingPlanAllocations;
};

export const getDynamicBabLinesByUUID = (
  lines: LineItem[],
  babUUID: string,
): LineItem[] => {
  const dynamicBabLines = lines.filter(
    (line) =>
      line.CustomAttributes?.find((attr) => attr.key === '_bundleUid')
        ?.value === babUUID && line.HideLine,
  );

  return dynamicBabLines;
};

const sumFormattedPrices = (
  dynamicPrice: string,
  linePrice: string,
): string => {
  const currency = get(window, ['Shopify', 'currency', 'active'], 'USD');
  const format = getCurrencyFormatter(currency);

  const dynamicPriceNumber =
    parseFloat(
      (`${dynamicPrice}`?.match(/\d+(,|.)\d+?/)?.[0] ?? dynamicPrice)?.replace(
        ',',
        '.',
      ),
    ) || 0;
  const linePriceNumber = parseFloat(
    (`${linePrice}`.match(/\d+(,|.)\d+?/)?.[0] ?? linePrice)?.replace(',', '.'),
  );

  return format(dynamicPriceNumber + linePriceNumber);
};

export const isDynamicLine = (line: LineItem) =>
  line?.CustomAttributes?.find(
    (attr) => attr.key === '_bundleType' && attr.value === 'dynamicBaB',
  );
export const isClassicBabLine = (line: LineItem): boolean =>
  !!line?.CustomAttributes?.find((attr) => attr.key === '_bundleBoxSize') &&
  !isDynamicLine(line);

export const isSameBaB = (line: LineItem, babUUId: string) => {
  const lineBaBUUID = line?.CustomAttributes?.find(
    (attr) => attr.key === '_bundleUid',
  )?.value;

  return lineBaBUUID === babUUId;
};

const getDynamicBabInLines = (dynamicItem: LineItem, acc: LineItem[]) => {
  const dynamicBabLine = acc.find(
    (line) =>
      line.BaBUUId ===
      dynamicItem.CustomAttributes?.find((attr) => attr.key === '_bundleUid')
        ?.value,
  );

  if (dynamicBabLine) return dynamicBabLine;

  const dynamicBabLineItem = { ...defaultLine, ...dynamicItem };
  dynamicBabLineItem.HideLine = false;
  dynamicBabLineItem.Quantity = 1;
  dynamicBabLineItem.ProductId =
    dynamicItem.CustomAttributes?.find(
      (attr) => attr.key === '_bundleProductId',
    )?.value || dynamicItem.ProductId;
  dynamicBabLineItem.Title =
    dynamicItem.CustomAttributes?.find((attr) => attr.key === '_bundleName')
      ?.value || '';
  dynamicBabLineItem.BaBUUId =
    dynamicItem.CustomAttributes?.find((attr) => attr.key === '_bundleUid')
      ?.value || '';

  dynamicBabLineItem.VariantTitle =
    dynamicItem.CustomAttributes?.find((attr) => attr.key === '_bundleLabel')
      ?.value || '';

  dynamicBabLineItem.CurrentPriceAmount = '0';
  dynamicBabLineItem.FormattedItemPrice = '0';
  dynamicBabLineItem.FormattedLineTotal = '0';
  dynamicBabLineItem.FormattedPricingPolicyBasePrice = '0';
  dynamicBabLineItem.FormattedPricingPolicyBasePriceMultipliedQuantiy = '0';
  dynamicBabLineItem.LineDiscountedPriceAmount = '0';
  dynamicBabLineItem.PricingPolicyBasePriceAmount = '0';

  return dynamicBabLineItem;
};

export const removeDynamicLineCustomAttributes = (
  dynamicItem: LineItem,
  line: LineItem,
): Record<string, string>[] => {
  const itemId = getIdFromGid(line.VariantId);
  const CUSTOM_ATTRIBUTES_PROPS = ['_bundleType', line.Title, `_pv_${itemId}`];

  return (
    (dynamicItem.CustomAttributes || [])
      // We remove old attributes for dynamic bab
      .filter(
        (customAttribute) =>
          !CUSTOM_ATTRIBUTES_PROPS.includes(customAttribute.key),
      )
  );
};

const isItemDupe = (dynamicItem: LineItem, line: LineItem) => {
  const itemId = getIdFromGid(line.VariantId);
  const isSameUuid = dynamicItem.BaBUUId === getLineBundleUUID(line)?.value;
  const isDupe = dynamicItem.CustomAttributes?.find(
    (attr) => attr.key === `_pv_${itemId}`,
  );
  return !!isDupe && isSameUuid;
};

const sumQuantity = (
  title: string,
  quantity: number,
  customAttributes: Record<string, string>[],
): number => {
  const quantityAttribute = customAttributes.find((attr) => attr.key === title)
    ?.value;

  return quantity + (quantityAttribute ? +quantityAttribute : 0);
};

const sumNewDynamicLine = (dynamicItem: LineItem, line: LineItem): LineItem => {
  const itemId = getIdFromGid(line.VariantId);

  return {
    ...dynamicItem,
    ...(isItemDupe ? { hasDuplicatedLines: true } : {}),
    BaBOutOfStockVariants: [
      ...(dynamicItem.BaBOutOfStockVariants || []),
      ...(line.BaBOutOfStockVariants || []),
    ],
    VariantId: (dynamicItem.CustomAttributes?.find(
      (attr) => attr.key === '_variantId',
    )?.value || line.VariantId) as string,
    CustomAttributes: [
      ...removeDynamicLineCustomAttributes(dynamicItem, line),
      { key: '_bundleType', value: 'dynamicBaB' },
      isItemDupe(dynamicItem, line)
        ? {
            key: line.Title,
            value: `${sumQuantity(
              line.Title,
              line.Quantity,
              dynamicItem.CustomAttributes,
            )}`,
          }
        : { key: line.Title, value: `${line.Quantity}` },
      isItemDupe(dynamicItem, line)
        ? {
            key: `_pv_${itemId}`,
            value: `${sumQuantity(
              `_pv_${itemId}`,
              line.Quantity,
              dynamicItem.CustomAttributes,
            )}`,
          }
        : { key: `_pv_${itemId}`, value: `${line.Quantity}` },
    ],
    CurrentPriceAmount: (
      Number(dynamicItem.CurrentPriceAmount) + Number(line.CurrentPriceAmount)
    ).toString(),
    LineDiscountedPriceAmount: (
      Number(dynamicItem.LineDiscountedPriceAmount) +
      Number(line.LineDiscountedPriceAmount)
    ).toString(),
    PricingPolicyBasePriceAmount: (
      Number(dynamicItem.PricingPolicyBasePriceAmount) +
      Number(line.PricingPolicyBasePriceAmount)
    ).toString(),
    FormattedItemPrice: sumFormattedPrices(
      dynamicItem.FormattedLineTotal,
      line.FormattedLineTotal,
    ),
    FormattedLineTotal: sumFormattedPrices(
      dynamicItem.FormattedLineTotal,
      line.FormattedLineTotal,
    ),
    FormattedPricingPolicyBasePrice: sumFormattedPrices(
      dynamicItem.FormattedPricingPolicyBasePriceMultipliedQuantiy as string,
      line.FormattedPricingPolicyBasePrice ||
        (line.PricingPolicyBasePriceAmount as string),
    ),
    FormattedPricingPolicyBasePriceMultipliedQuantiy:
      line.FormattedPricingPolicyBasePriceMultipliedQuantiy
        ? sumFormattedPrices(
            dynamicItem.FormattedPricingPolicyBasePriceMultipliedQuantiy as string,
            line.FormattedPricingPolicyBasePriceMultipliedQuantiy as string,
          )
        : sumFormattedPrices(
            dynamicItem.FormattedPricingPolicyBasePriceMultipliedQuantiy as string,
            `${
              Number(line.PricingPolicyBasePriceAmount) * line.Quantity
            }` as string,
          ),
  };
};

export const wrapDynamicBabItems = (
  subscription: Subscription,
): Subscription => {
  if (!hasDynamicLines(subscription.Lines)) return subscription;

  const newSub = {
    ...subscription,
    Lines: wrapDynamicLines(subscription.Lines),
  };
  return newSub;
};

const hasDynamicLines = (lines: LineItem[] | null | undefined): boolean => {
  if (!lines || !lines.length) return false;
  return lines.some((line) => {
    const dynamicItem = isDynamicLine(line);
    return !!dynamicItem;
  });
};

const cleanRemovedItems = (
  lines: LineItem[],
  cAttrs?: CustomAttributes,
  baBUUId = '',
): CustomAttributes => {
  return cAttrs?.filter((attr) => {
    if (!attr.key.startsWith('_pv_')) return true;

    const line = lines.find(
      ({ VariantId, ...line }) =>
        `_pv_${getIdFromGid(VariantId)}` === attr.key &&
        isSameBaB(line as LineItem, baBUUId),
    );

    const lineNoLongerExistsOrItsNotABundleItem =
      !line ||
      (line &&
        !line.CustomAttributes?.find((attr) => attr.key === '_bundleHandle'));

    return !lineNoLongerExistsOrItsNotABundleItem;
  });
};

export const wrapDynamicLines = (
  lines: LineItem[] | null | undefined,
): LineItem[] => {
  return (
    lines?.reduce((acc, line) => {
      if (!isDynamicLine(line)) return [...acc, line];

      let dynamicItem = getDynamicBabInLines(line, acc);

      dynamicItem = sumNewDynamicLine(dynamicItem, line);

      dynamicItem.CustomAttributes = cleanRemovedItems(
        lines,
        dynamicItem.CustomAttributes,
        dynamicItem.BaBUUId,
      );

      remove(acc, (l) => l.BaBUUId === dynamicItem.BaBUUId);

      line.HideLine = true;

      return [...acc, dynamicItem, line];
    }, [] as LineItem[]) ?? []
  );
};

const generateUpdateLine = (
  line: any,
  babUUID: string,
  oneTimeAddon = false,
) => {
  const customAttributes = Object.entries(line.properties).map(
    ([key, value]) => {
      if (key === '_bundleUid') {
        return {
          key: '_bundleUid',
          value: babUUID,
        };
      }
      return {
        key,
        value: `${value}`,
      };
    },
  );

  return {
    id: line.SubscriptionContractLineId,
    input: {
      quantity: line.quantity,
      customAttributes: customAttributes,
      productVariantId: getGidFromIdForResource(line.id, 'ProductVariant'),
      lineSellingPolicy: oneTimeAddon ? 'ONETIME' : 'SUBSCRIPTION',
      isAddon: oneTimeAddon,
    },
  };
};

const generateDeleteLine = (line: any) => {
  return {
    id: line.SubscriptionContractLineId,
    input: {
      quantity: 0,
    },
  };
};

export const getBabUUID = (line: LineItem) => {
  return (
    line?.CustomAttributes?.find((attr) => attr.key === '_bundleUid')?.value ||
    ''
  );
};

const findBundleUUID = (subscription: Subscription, lineId: string): string => {
  const line = subscription.Lines?.find(
    (line: LineItem) =>
      line.SubscriptionContractLineId === lineId && line.BaBUUId,
  );
  return line?.BaBUUId || '';
};

const findLinesToUpdate = (
  itemsInBab: ProductWithProps[],
  subscription: Subscription,
  bundleUUID: string,
) => {
  const linesToUpdate = itemsInBab?.map((babLine) => {
    const subsLine = subscription.Lines?.find(
      (subscriptionLine) =>
        getIdFromGid(subscriptionLine.VariantId) === babLine.id &&
        isDynamicLine(subscriptionLine) &&
        isSameBaB(subscriptionLine, bundleUUID),
    )?.SubscriptionContractLineId;

    return {
      ...babLine,
      SubscriptionContractLineId: subsLine,
    };
  });

  return linesToUpdate;
};

const findLinesToDelete = (
  babCurrentProducts: string[],
  subscription: Subscription,
  bundleUUID: string,
): { id: string; input: any }[] => {
  const lines = subscription.Lines ?? [];
  const linesToDelete = lines.filter(
    (line) =>
      !babCurrentProducts.includes(line.VariantId) &&
      isSameBaB(line, bundleUUID) &&
      !line.BaBUUId,
  );
  return linesToDelete.map(generateDeleteLine);
};

export const generateNewDynamicSubscriptionLines = (
  subscription: Subscription,
  itemsInBab: ProductWithProps[],
  lineId: string,
  oneTimeAddon,
): {
  id: string;
  input: any;
}[] => {
  // Find the bundle UUID for the line
  const bundleUUID = findBundleUUID(subscription, lineId);

  // If the line is not a bundle line, return an empty array
  if (!bundleUUID) {
    return [];
  }

  // Find the lines to update
  const linesToUpdate =
    findLinesToUpdate(itemsInBab, subscription, bundleUUID) || [];

  // Update lines of the bundle with new quantities and the correct attributes.
  const updatedLines = linesToUpdate.map((line) =>
    generateUpdateLine(line, bundleUUID, oneTimeAddon),
  );

  // Get a list of the GIDs for the items in the BaB
  const babCurrentProducts = itemsInBab.map((item) =>
    getGidFromIdForResource(item.id, 'ProductVariant'),
  );

  // Find the lines of the Bab that has been deleted.
  const linesToDelete = findLinesToDelete(
    babCurrentProducts,
    subscription,
    bundleUUID,
  );

  // Return an array of lines to update and delete
  return uniqBy([...updatedLines, ...linesToDelete], 'id');
};

const FIXED_AMOUNT_DISCOUNT = 'FIXED_AMOUNT';
const PERCENTAGE_DISCOUNT = 'PERCENTAGE';

export const getPricingPolicyCycleDiscount = (
  pricingPolicyCycleDiscount?: PricingPolicyCycleDiscount[],
  successfulBillingAttempts?: string[],
): PricingPolicyCycleDiscount | undefined => {
  if (!pricingPolicyCycleDiscount || isEmpty(pricingPolicyCycleDiscount)) {
    return;
  }

  const cycles = successfulBillingAttempts?.length || 0;

  let discount;

  for (const d of pricingPolicyCycleDiscount) {
    if (d.afterCycle >= (discount?.afterCycle || 0) && d.afterCycle <= cycles) {
      discount = d;
    }
  }

  return discount || pricingPolicyCycleDiscount[0];
};

export const getAddonDiscount = (
  addonDiscount?: AddonsDiscounts,
):
  | {
      type: string;
      value: number;
    }
  | undefined => {
  if (!addonDiscount) {
    return;
  }

  if (addonDiscount.type === FIXED_AMOUNT_DISCOUNT) {
    return {
      type: addonDiscount.type,
      value: toNumber(addonDiscount.amount),
    };
  }
  if (addonDiscount.type === PERCENTAGE_DISCOUNT) {
    return {
      type: addonDiscount.type,
      value: toNumber(addonDiscount.percentage),
    };
  }

  if (
    isUndefined(addonDiscount.type) &&
    (addonDiscount.amount || addonDiscount.percentage)
  ) {
    return {
      type: addonDiscount.amount ? FIXED_AMOUNT_DISCOUNT : PERCENTAGE_DISCOUNT,
      value: toNumber(addonDiscount.amount || addonDiscount.percentage),
    };
  }
};

export const getDiscount = (
  ppDiscount: PricingPolicyCycleDiscount | undefined,
):
  | {
      type: string;
      value: number;
    }
  | undefined => {
  if (!ppDiscount) return;
  const { adjustmentType, adjustmentValue } = ppDiscount;
  const value =
    adjustmentType === FIXED_AMOUNT_DISCOUNT
      ? Number(adjustmentValue['amount'])
      : Number(adjustmentValue['percentage']) || 0;
  return {
    type: adjustmentType,
    value,
  };
};

function replacePrice(
  priceString: string,
  newPrice: number,
): string | undefined {
  return priceString?.replace(/\d+[.,]?\d*/g, `${newPrice}`);
}

export const getBaBLines = (line, subscription, apiProductVariants) => {
  let productVariants: {
    variantId: string;
    title: string;
    quantity: number;
    scLine?: any;
    image?: string;
  }[] = [];
  const isDynamicBaBLine = !!isDynamicLine(line);
  const babUUId = line.BaBUUId;

  const productVariantIds =
    line.CustomAttributes?.filter(({ key }) => key.startsWith('_pv_')).map(
      (productVariantId) => ({
        ...productVariantId,
        key: productVariantId.key.replace('_pv_', ''),
      }),
    ) || [];

  const isOneProductDeleted = !!apiProductVariants?.some(
    ({ title, product }) => title === null || isEmpty(product),
  );

  //Get the variants names and quantities saved in CustomAttributes
  let babProductsTitlesAndQuantities = line.CustomAttributes?.filter(
    ({ key }) => !key.startsWith('_bundle'),
  ).filter(({ key }) => !key.startsWith('_pv'));

  for (const variantId of productVariantIds) {
    const foundProductVariant = apiProductVariants.find(
      ({ gId }) =>
        gId === getGidFromIdForResource(variantId.key, 'ProductVariant'),
    );
    const isDuplicated =
      subscription.Lines?.filter(
        (l) =>
          getIdFromGid(l.VariantId) === variantId.key && isSameBaB(l, babUUId),
      ).length > 1;

    const quantity = subscription.Lines?.reduce((acc, l) => {
      if (
        getIdFromGid(l.VariantId) === variantId.key &&
        isSameBaB(l, babUUId)
      ) {
        return acc + l.Quantity;
      }
      return acc;
    }, 0);

    let title = '';
    let variantTitle;

    /*
    If there is at least one product deleted we have to get all the information from CustomAttributes
    Because we could be mapping the name and quantity wrongly
     */
    if (isOneProductDeleted) {
      /*
      Find the first occurence of the variant name based on the quantity
      This is because the only thing that connects the variantId with the variant name
      is the quantity.
      */
      const classicBaBvariantTitleIndex =
        babProductsTitlesAndQuantities.findIndex(
          ({ value }) => variantId.value === value,
        );
      const classicBaBvariantTitle =
        babProductsTitlesAndQuantities[classicBaBvariantTitleIndex];

      //Remove the found element so we dont find the same name based on quantity
      babProductsTitlesAndQuantities = babProductsTitlesAndQuantities.filter(
        (_, i) => i !== classicBaBvariantTitleIndex,
      );
      title = classicBaBvariantTitle?.key;
    } else {
      const originVariantTitle = foundProductVariant?.title;

      variantTitle =
        originVariantTitle !== 'Default Title' ? `- ${originVariantTitle}` : '';
      title = `${foundProductVariant?.product?.title} ${variantTitle}`;
    }

    const image =
      foundProductVariant?.image?.url ||
      foundProductVariant?.product?.featuredImage?.url ||
      line?.VariantImageSrc;

    let newVariant: {
      variantId: string;
      title: string;
      quantity: number;
      scLine?: any;
      image?: string;
    } = {
      variantId: variantId.key,
      title,
      quantity: quantity || variantId.value,
      image,
    };

    if (isDynamicBaBLine) {
      const dynamicBaBVariantId = getGidFromIdForResource(
        variantId.key,
        'ProductVariant',
      );

      const scLine = subscription.Lines.find(
        ({ VariantId, BaBUUId }) =>
          VariantId === dynamicBaBVariantId && !BaBUUId,
      );

      if (isDuplicated) {
        const totalDiscountedPrice =
          parseFloat(get(scLine, 'CurrentPriceAmount', '0')) * quantity;
        const totalOriginalPrice =
          parseFloat(get(scLine, 'PricingPolicyBasePriceAmount', '0')) *
          quantity;

        scLine.FormattedPricingPolicyBasePriceMultipliedQuantiy = replacePrice(
          scLine.FormattedPricingPolicyBasePriceMultipliedQuantiy,
          totalOriginalPrice,
        );
        scLine.FormattedLineTotal = replacePrice(
          scLine.FormattedLineTotal,
          totalDiscountedPrice,
        );
      }

      const foundVariantImage = apiProductVariants.find(
        ({ gId }) => gId === dynamicBaBVariantId,
      );

      const image =
        foundVariantImage?.image?.url ||
        foundVariantImage?.product?.featuredImage?.url ||
        scLine?.VariantImageSrc;

      newVariant = {
        ...newVariant,
        title: `${foundVariantImage?.product?.title || scLine?.Title} ${
          variantTitle || scLine?.VariantTitle
        }`,
        scLine,
        image,
      };
    } else {
      newVariant = {
        ...newVariant,
        scLine: true,
      };
    }

    productVariants = [...productVariants, newVariant];
  }

  return _uniqBy(productVariants, 'variantId');
};

export const getDynamicSubscriptionLinePrices = ({
  lines = [],
}: {
  lines: LineItem[];
}): Record<string, any> => {
  const productLines = lines.filter((l) => !l.BaBUUId);
  return productLines.reduce(
    (acc, line) => {
      const { CurrentPriceAmount, PricingPolicyBasePriceAmount, VariantId } =
        line;
      acc[VariantId] = {
        CurrentPriceAmount: toNumber(CurrentPriceAmount),
        PricingPolicyBasePriceAmount: toNumber(PricingPolicyBasePriceAmount),
      };

      return acc;
    },
    {} as Record<string, any>,
  );
};

export const formatProductTitle = (fullName: string): JSX.Element => {
  const [productTitle, variantTitle] = fullName.split(' - ');

  return (
    <>
      <span className="awt-product-title">{productTitle}</span>
      {variantTitle && (
        <>
          <span className="awt-dash"> - </span>
          <span className="awt-variant-title">{variantTitle}</span>
        </>
      )}
    </>
  );
};
